import type { ColumnsType } from 'antd/lib/table';
import { format } from 'date-fns';
import { tableDateTimeFormat } from '../../../lib/utils/date-time';

const tableColumnProps = (
  title: string,
  dataIndex: string,
  render?: (value: Date) => string
) => ({
  title,
  dataIndex,
  key: dataIndex,
  render,
});

const dateRender = (value?: Date) =>
  value === undefined ? '' : format(value, tableDateTimeFormat);

///////////////////////
// Shift table props //
///////////////////////
export type ShiftTableRow = {
  readonly key: string;
  readonly teamName: string;
  readonly teamType: string;
  readonly teamIon: string;
  readonly roleName: string;
  readonly roleGroup: string;
  readonly username: string;
  readonly userFirstName: string;
  readonly userLastName: string;
  readonly userEmploymentType: string;
  readonly shiftStatus: string;
  readonly shiftDateCreated: Date;
  readonly shiftStartTime: Date;
  readonly shiftEndTime: Date;
};

/**
 * The column headers for shift table.
 * @param includeUserColumns - whether to include user columns
 * @returns shift table columns
 */
export const shiftTableColumns = (
  includeUserColumns: boolean
): ColumnsType<ShiftTableRow> => [
  tableColumnProps('Team Name', 'teamName'),
  tableColumnProps('Team Type', 'teamType'),
  tableColumnProps('Internal Order Number', 'teamIon'),
  tableColumnProps('Team Role', 'roleName'),
  tableColumnProps('Team Role Group', 'roleGroup'),
  ...(includeUserColumns
    ? [
        tableColumnProps('Username (user email)', 'username'),
        tableColumnProps('User First Name', 'userFirstName'),
        tableColumnProps('User Last Name', 'userLastName'),
        tableColumnProps('User Employment Type', 'userEmploymentType'),
      ]
    : []),
  tableColumnProps('Shift Status', 'shiftStatus'),
  tableColumnProps('Shift Created At', 'shiftDateCreated', dateRender),
  tableColumnProps('Shift Start Time', 'shiftStartTime', dateRender),
  tableColumnProps('Shift End Time', 'shiftEndTime', dateRender),
];

/////////////////////////////
// Shift event table props //
/////////////////////////////
export type ShiftEventTableRow = {
  readonly key: string;
  readonly shiftId: string;
  readonly shiftEvent: string;
  readonly eventCreatedAt: Date;
  readonly eventCreatedBy: string;
  readonly shiftStartTime: Date;
  readonly shiftEndTime: Date;
  readonly shiftRecurrencePattern: string;
  readonly shiftRecurrenceEndDate?: Date;
  readonly shiftStatus: string;
  readonly shiftNote?: string;
  readonly username?: string;
  readonly userFullName: string;
  readonly userEmploymentType?: string;
  readonly roleName: string;
  readonly roleGroup?: string;
  readonly teamName: string;
  readonly teamType: string;
  readonly teamIon?: string;
};

export const shiftEventTableColumns: ColumnsType<ShiftEventTableRow> = [
  tableColumnProps('Shift Id', 'shiftId'),
  tableColumnProps('Shift Event', 'shiftEvent'),
  tableColumnProps('Shift Event Created At', 'eventCreatedAt', dateRender),
  tableColumnProps('Shift Event Created By', 'eventCreatedBy'),
  tableColumnProps('Shift Start Time', 'shiftStartTime', dateRender),
  tableColumnProps('Shift End Time', 'shiftEndTime', dateRender),
  tableColumnProps('Shift Recurrence Pattern', 'shiftRecurrencePattern'),
  tableColumnProps(
    'Shift Recurrence End Date',
    'shiftRecurrenceEndDate',
    dateRender
  ),
  tableColumnProps('Shift Status', 'shiftStatus'),
  tableColumnProps('Shift Note', 'shiftNote'),
  tableColumnProps('Username (user email)', 'username'),
  tableColumnProps('User Name', 'userFullName'),
  tableColumnProps('User Employment Type', 'userEmploymentType'),
  tableColumnProps('Team Role', 'roleName'),
  tableColumnProps('Team Role Group', 'roleGroup'),
  tableColumnProps('Team Name', 'teamName'),
  tableColumnProps('Team Type', 'teamType'),
  tableColumnProps('Internal Order Number', 'teamIon'),
];
